import React, { useEffect, useState } from 'react';
import '../css/Leaderboard.css';
import { firestore, auth } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, orderBy, limit } from 'firebase/firestore';
import { showErrorToast } from '../utils/toastUtils';
import { rateLimiter } from '../utils/simpleRateLimiter';

const Leaderboard = ({ onClose }) => {
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('friends'); // Default to friends tab

    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                // Check rate limit for leaderboard updates
                if (!rateLimiter.canMakeRequest(auth.currentUser.uid)) {
                    throw new Error("Too many leaderboard updates. Please wait a moment before trying again.");
                }

                const userId = auth.currentUser?.uid;
                if (!userId) {
                    setLoading(false);
                    return;
                }

                if (activeTab === 'friends') {
                    // Get current user's friends
                    const friendsQuery = query(
                        collection(firestore, "Friends"),
                        where("status", "==", "accepted"),
                        where("sender", "==", userId)
                    );
                    const friendsQuery2 = query(
                        collection(firestore, "Friends"),
                        where("status", "==", "accepted"),
                        where("receiver", "==", userId)
                    );

                    const [friendsSnapshot1, friendsSnapshot2] = await Promise.all([
                        getDocs(friendsQuery),
                        getDocs(friendsQuery2)
                    ]);

                    // Get friend IDs
                    const friendIds = [
                        ...friendsSnapshot1.docs.map(doc => doc.data().receiver),
                        ...friendsSnapshot2.docs.map(doc => doc.data().sender)
                    ];

                    // Get current user's data
                    const currentUserDoc = await getDoc(doc(firestore, "users", userId));
                    const currentUserData = currentUserDoc.data();

                    // Get all friend data from users collection
                    const friendDocs = await Promise.all(
                        friendIds.map(id => getDoc(doc(firestore, "users", id)))
                    );

                    // Process friend data with current points
                    const friendsData = friendDocs
                        .filter(doc => doc.exists())
                        .map(doc => ({
                            userId: doc.id,
                            username: doc.data().username || doc.data().email,
                            points: doc.data().points || 0
                        }));

                    // Add current user to the list
                    const allUsers = [
                        ...friendsData,
                        {
                            userId: userId,
                            username: currentUserData?.username || currentUserData?.email,
                            points: currentUserData?.points || 0
                        }
                    ];

                    // Sort by points
                    const sortedUsers = allUsers.sort((a, b) => b.points - a.points);
                    setLeaderboard(sortedUsers);
                } else {
                    // Fetch top 10 users globally
                    const usersQuery = query(
                        collection(firestore, "users"),
                        orderBy("points", "desc"),
                        limit(10)
                    );
                    
                    const usersSnapshot = await getDocs(usersQuery);
                    const globalUsers = usersSnapshot.docs.map(doc => ({
                        userId: doc.id,
                        username: doc.data().username || doc.data().email,
                        points: doc.data().points || 0
                    }));
                    
                    setLeaderboard(globalUsers);
                }

                setLoading(false);
            } catch (error) {
                showErrorToast(error.message);
                setLoading(false);
            }
        };

        fetchLeaderboard();
    }, [activeTab]);

    if (loading) {
        return (
            <div className="leaderboard-overlay">
                <div className="leaderboard-modal">
                    <h2>Loading Leaderboard...</h2>
                </div>
            </div>
        );
    }

    return ( 
        <div className="leaderboard-overlay">
            <div className="leaderboard-modal">
                <h2>Leaderboard</h2>
                <button className="close-btn" onClick={onClose}>✖</button>
                
                <div className="leaderboard-tabs">
                    <button 
                        className={`tab-btn ${activeTab === 'friends' ? 'active' : ''}`}
                        onClick={() => setActiveTab('friends')}
                    >
                        Friends
                    </button>
                    <button 
                        className={`tab-btn ${activeTab === 'global' ? 'active' : ''}`}
                        onClick={() => setActiveTab('global')}
                    >
                        Global Top 10
                    </button>
                </div>

                <ul className="leaderboard-list">
                    {leaderboard.map((player, index) => (
                        <li key={player.userId} className="leaderboard-item">
                            <span className="rank">
                                {index === 0 ? (
                                    <span className="crown">👑</span>
                                ) : null}
                                #{index + 1}
                            </span>
                            <span className="username">{player.username}</span>
                            <span className="score">{player.points} pts</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default Leaderboard;